/**
 * Client side of the Stage 3 asset-sync pipeline. Fetches the
 * server-published asset manifest from CloudFront, caches it in
 * localStorage so cold-launch hits don't always wait on the network,
 * and exposes a {@link resolveCdnUrl} helper the
 * {@link BiomePackManager} uses to swap bundled URLs for versioned
 * CDN paths.
 *
 * <h2>Manifest schema</h2>
 *
 * <p>Mirrors the server-side {@code AssetManifest} record from
 * {@code goodtime-server}: a flat path → {sha256, size, mtimeMs} map
 * plus the version + builtAt timestamp. The pointer object that
 * lives at {@code &lt;cdnBase&gt;/manifest-latest.json} is what the client
 * actually fetches; per-version snapshots at
 * {@code &lt;cdnBase&gt;/v&lt;N&gt;/manifest.json} are immutable historical
 * records.
 *
 * <h2>Fallback strategy</h2>
 *
 * <p>Cache wins over network on cold launch — we return whatever's
 * in localStorage immediately + kick off a background refresh. If
 * the refresh fetches a newer version, future asset loads pick it up;
 * in-flight ones stay on the old version (they've already resolved
 * URLs). If the manifest is missing entirely (first launch +
 * offline, no fetch ever succeeded), {@link resolveCdnUrl} returns
 * null and callers fall back to bundled {@code src} URLs — the app
 * still works, it just loads from the install bundle.
 *
 * <h2>Module shape</h2>
 *
 * <p>Singleton state under {@link AssetManifestClient.shared}.
 * Initialised once at app boot via {@link initManifestClient}; calls
 * before init return {@code null} from {@link resolveCdnUrl} so
 * pre-boot code paths transparently use the fallback.
 */
export interface AssetManifest {
  version: number;
  builtAtMs: number;
  entries: Record<string, AssetManifestEntry>;
}

export interface AssetManifestEntry {
  sha256: string;
  size: number;
  mtimeMs: number;
}

/** Local storage key — versioned with a schema-version suffix so a
 *  future shape change can ignore stale records without conflicting. */
const STORAGE_KEY = 'goodtime.assetManifest.v1';

/** How fresh the cached manifest must be before we skip the
 *  background refresh entirely. 5 minutes keeps reactive scroll-fast
 *  launches cheap while still picking up overnight deploys within
 *  one session. */
const FRESHNESS_MS = 5 * 60 * 1000;

interface CachedRecord {
  fetchedAtMs: number;
  cdnBase: string;
  manifest: AssetManifest;
}

export class AssetManifestClient {
  private cdnBase: string | null = null;
  private manifest: AssetManifest | null = null;
  /** Pending fetch — concurrent {@link init} calls share the same
   *  network round-trip. */
  private inflight: Promise<void> | null = null;
  /** Stage 3 dev-only fallback. When set (typically to
   *  {@code '/src/assets'} from the mobile app boot when no
   *  CDN base is configured), {@link resolveCdnUrl} returns
   *  {@code <fallbackPrefix>/<path>} for unresolved paths, so the
   *  Vite dev server serves the file directly from disk and
   *  designers don't need a CDN to iterate locally. Production
   *  builds leave this null and let unresolved paths fall through
   *  to null → AssetLoader skip. */
  private fallbackPrefix: string | null = null;

  /** Boot-time entry point. Loads any cached manifest from
   *  localStorage synchronously, kicks off a background refresh from
   *  {@code cdnBase}, and resolves once the refresh either succeeds
   *  or fails (silently — see fallback strategy in class doc).
   *
   *  <p>If {@code cdnBase} is null/empty the client stays inert (the
   *  Stage 2 "everything bundled" behaviour); {@link resolveCdnUrl}
   *  returns null for every call and packs load from {@code src}.
   *
   *  <p>The returned promise is "best effort" — the caller can await
   *  it to make sure the first pack download uses the latest URLs,
   *  but it's safe to fire-and-forget if you want the app to render
   *  immediately on a slow/offline boot. */
  async init(
    cdnBase: string | null | undefined,
    options?: { fallbackPrefix?: string; devManifestUrl?: string },
  ): Promise<void> {
    // Stage 3 — even with no CDN base, the dev fallback prefix can
    // be set so {@link resolveCdnUrl} returns Vite-dev-server URLs.
    // This lets local dev work without a CloudFront round-trip while
    // production stays strict (null → AssetLoader skip).
    this.fallbackPrefix = options?.fallbackPrefix ?? null;
    if (!cdnBase) {
      // Dev-only: a Vite middleware can serve a manifest generated from
      // src/assets, letting manifest-driven dev UIs (e.g. the
      // ?dev=biomes preview) enumerate assets without a CDN.
      // resolveCdnUrl still routes through fallbackPrefix (/src/assets),
      // never a versioned CDN URL.
      if (options?.devManifestUrl) {
        try {
          const r = await fetch(options.devManifestUrl, { cache: 'no-cache' });
          if (r.ok) {
            const m = (await r.json()) as AssetManifest;
            if (m?.entries) this.manifest = m;
          }
        } catch (err) {
          console.warn('[AssetManifestClient] dev manifest load failed', err);
        }
      }
      return;
    }
    this.cdnBase = cdnBase;

    // Load cache synchronously so the first {@link resolveCdnUrl}
    // call (potentially right after init returns) sees something.
    const cached = this.readCache();
    if (cached && cached.cdnBase === cdnBase) {
      this.manifest = cached.manifest;
      // Cache is fresh enough — skip the background refresh.
      if (Date.now() - cached.fetchedAtMs < FRESHNESS_MS) {
        return;
      }
    }

    // Refresh. Share across concurrent boots (strict-mode double-mount).
    if (this.inflight) {
      await this.inflight;
      return;
    }
    this.inflight = this.fetchAndCache(cdnBase)
      .catch((err) => {
        // Silent fallback — the cached manifest (if any) stays in
        // place; the bundled {@code src} kicks in when there is no
        // cached manifest at all.
        console.warn('[AssetManifestClient] manifest refresh failed', err);
      })
      .finally(() => {
        this.inflight = null;
      });
    await this.inflight;
  }

  /** Resolve a repo-relative asset path against the loaded manifest.
   *  Returns the full CDN URL (e.g.
   *  {@code https://d3....cloudfront.net/v3/reel/royal/crown.png})
   *  when the path is in the manifest + the CDN base is configured.
   *  Otherwise returns null and callers fall back to bundled {@code
   *  src}.
   *
   *  <p>Cheap — pure map lookup + string concat, safe to call per
   *  asset during a pack download. */
  resolveCdnUrl(relativePath: string): string | null {
    if (this.cdnBase && this.manifest?.entries[relativePath]) {
      // A PNG or JPEG with a published WebP copy (scripts/webp-assets.sh)
      // loads as the WebP: same art at roughly a tenth of the bytes.
      const webp = /\.(png|jpg)$/.test(relativePath) ? relativePath.replace(/\.(png|jpg)$/, '.webp') : null;
      const file = webp && this.manifest.entries[webp] ? webp : relativePath;
      return `${this.cdnBase}/v${this.manifest.version}/${file}`;
    }
    // Stage 3 dev fallback — when the mobile app boot configured a
    // fallback prefix (typically '/src/assets' under Vite dev), use
    // it so unbundled CDN-only entries still render in dev.
    if (this.fallbackPrefix) {
      return `${this.fallbackPrefix}/${relativePath}`;
    }
    return null;
  }

  /** Latest known manifest, or null when none is loaded yet. Exposed
   *  for diagnostics + future "first-launch progress" UI that needs
   *  to know total pack size before fetching. */
  getManifest(): AssetManifest | null {
    return this.manifest;
  }

  private readCache(): CachedRecord | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CachedRecord;
      if (!parsed.manifest?.entries) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private writeCache(record: CachedRecord): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Quota-exceeded / private mode etc. — non-fatal.
    }
  }

  private async fetchAndCache(cdnBase: string): Promise<void> {
    const url = `${cdnBase}/manifest-latest.json`;
    const response = await fetch(url, { cache: 'no-cache' });
    if (!response.ok) {
      throw new Error(`manifest-latest.json HTTP ${response.status}`);
    }
    const manifest = (await response.json()) as AssetManifest;
    if (!manifest?.entries || typeof manifest.version !== 'number') {
      throw new Error('manifest payload missing required fields');
    }
    this.manifest = manifest;
    this.writeCache({
      fetchedAtMs: Date.now(),
      cdnBase,
      manifest,
    });
  }

  static readonly shared = new AssetManifestClient();
}

/** Boot-time helper — initialises the shared client. Call once from
 *  the app's top-level mount (e.g. App.tsx). Safe to await or
 *  fire-and-forget; see {@link AssetManifestClient.init}.
 *
 *  <p>{@code cdnBase} comes from the build-time env var
 *  {@code VITE_ASSET_CDN_BASE}. Empty / undefined leaves the client
 *  inert (bundled-everything path stays active). */
export function initManifestClient(
  cdnBase: string | null | undefined,
  options?: { fallbackPrefix?: string; devManifestUrl?: string },
): Promise<void> {
  return AssetManifestClient.shared.init(cdnBase, options);
}

/** Resolve a {@link ThemeAssetEntry} to its effective load URL —
 *  the CDN-versioned URL when the manifest knows about
 *  {@code entry.path}, otherwise the bundled {@code entry.src}.
 *  Single point of policy for both {@link AssetLoader} and
 *  {@link BiomePackManager} so they stay in lockstep on which URL
 *  Pixi / Howler ends up fetching.
 *
 *  <p>Callers don't need to know the manifest exists — passing a
 *  Stage-2 entry (no {@code path}) is a no-op that just returns
 *  {@code src}. Callers also don't need to await
 *  {@link initManifestClient}; pre-init returns null which falls
 *  through to {@code src}. */
export function resolveEntryUrl(entry: {
  src?: string;
  path?: string;
}): string | null {
  // {@link resolveCdnUrl} folds in the dev fallback (when
  // {@link initManifestClient} was configured with a fallbackPrefix),
  // so this helper stays a thin priority list: CDN/fallback > src > null.
  if (entry.path) {
    const url = AssetManifestClient.shared.resolveCdnUrl(entry.path);
    if (url) return url;
  }
  return entry.src ?? null;
}
